<div class="h-screen flex flex-col overflow-hidden">

  <!-- Report Header -->
  <app-report-header
    (dataEmitter)="receiveSidebarTabs($event)"
    (dataEmitter2)="receiveAside($event)"
    (asideStateChange)="handleAsideStateChange($event)">
  </app-report-header>

  <div class="bg-white rounded-tl-lg rounded-tr-lg w-full p-6 flex-1 overflow-y-auto">

    <div class="mx-auto w-full max-w-7xl px-0 sm:px-6 lg:px-8 py-8">

      <!-- Market Insight Header Banner -->
      <div class="bg-gradient-to-r from-blue-700 to-blue-600 rounded-xl w-full px-8 py-10 mb-6">
        <div class="flex justify-between items-center">
          <!-- Left Section -->
          <div>
            <h1 class="text-white text-4xl font-bold mb-2">Market Insight</h1>
            <p *ngIf="!(error$ | async)" class="text-blue-100 text-lg">Prepared exclusively for {{ client$ | async }}</p>
            <p *ngIf="(error$ | async)" class="text-blue-100 text-lg">Report could not be loaded</p>
          </div>

          <!-- Right Section -->
          <div class="text-right">
            <h2 class="text-white text-2xl font-semibold mb-1">SalesOMMO</h2>
            <p class="text-blue-200 text-sm">#NoBS B2B Marketing</p>
          </div>
        </div>
      </div>

      <!-- Loading State -->
      <div *ngIf="(loading$ | async)" class="bg-gray-50 rounded-xl p-8 mb-6 text-center">
        <div class="inline-flex items-center">
          <svg class="animate-spin -ml-1 mr-3 h-5 w-5 text-blue-600" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
            <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
            <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
          </svg>
          <span class="text-base text-secondary">Loading market insights...</span>
        </div>
      </div>

      <!-- Error State -->
      <div *ngIf="(error$ | async) && !(loading$ | async)" class="bg-gray-50 rounded-xl p-8 mb-6 text-center">
        <div class="mb-6">
          <svg class="mx-auto h-16 w-16 text-secondary mb-4" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <h2 class="text-2xl font-bold text-secondary mb-2">Unable to Generate Report</h2>
          <p class="text-base text-secondary mb-6">{{ extractErrorMessage(error$ | async) }}</p>
        </div>

        <!-- Retry Options -->
        <div class="flex flex-col sm:flex-row gap-4 justify-center">
          <button
            (click)="loadMarketingReport(user.email)"
            [disabled]="(loading$ | async)"
            class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
          >
            <span *ngIf="!(loading$ | async)">Try Again</span>
            <span *ngIf="(loading$ | async)">Retrying...</span>
          </button>
        </div>
      </div>

      <!-- Report Content (only show if no error and not loading) -->
      <div *ngIf="!(error$ | async) && !(loading$ | async)" class="bg-gray-50 rounded-xl p-6">

        <!-- About This Document -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">
          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">About This Document</h2>

          <!-- Document Details Grid -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

            <!-- Creation Date -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">Creation Date</h3>
              <p class="text-base text-secondary">{{ creationDate$ | async }}</p>
            </div>

            <!-- Creator -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">Creator</h3>
              <p class="text-base text-secondary">{{ creator$ | async }}</p>
            </div>

            <!-- Client -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">Client</h3>
              <p class="text-base text-secondary">{{ client$ | async }}</p>
            </div>

            <!-- Client Website -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">Client Website</h3>
              <a [href]="'https://' + (clientWebsite$ | async)" target="_blank" class="text-base text-blue-600 hover:text-blue-700">
                {{ clientWebsite$ | async }}
              </a>
            </div>

            <!-- Scope Section -->
            <div class="col-span-2 bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">Scope</h3>
              <p class="text-base text-secondary">{{ scope$ | async }}</p>
            </div>

          </div>

          <!-- Welcome Message -->
          <div class="pt-6 border-t border-gray-100">
            <p class="text-base text-secondary leading-relaxed">{{ welcomeMessage$ | async }}</p>
          </div>
        </div>
        <!-- / About This Document -->

        <!-- Why You Should Read This Document -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Why You Should Read This Document</h2>

          <!-- Description Paragraph -->
          <p class="text-base text-secondary mb-6">
            {{ whyReadDescription$ | async }}
          </p>

          <!-- Companies Analyzed Highlight Box -->
          <div class="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <p class="text-base text-secondary">
              We analyzed <span class="font-medium text-blue-600">{{ companiesAnalyzed$ | async }} companies</span> in the industry to provide you with actionable intelligence based on real market data.
            </p>
          </div>

          <!-- Data-driven Approach Description -->
          <p class="text-base text-secondary mb-6">
            {{ dataApproachDescription$ | async }}
          </p>

          <!-- Next Steps Highlight Box -->
          <div class="bg-blue-50 border border-blue-100 rounded-xl p-6">
            <p class="text-base text-secondary font-semibold">
              {{ nextStepsDescription$ | async }}
            </p>
          </div>

        </div>
        <!-- / Why You Should Read This Document -->

        <!-- Client Descriptors -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Client Descriptors</h2>

          <!-- Client Description -->
          <p class="text-base text-secondary leading-relaxed mb-6">
            {{ clientDescription$ | async }}
          </p>

          <!-- Client Details Box -->
          <div class="bg-blue-50 border border-blue-200 rounded-xl p-6">
            <p class="text-base text-secondary leading-relaxed">
              {{ clientDetails$ | async }}
            </p>
          </div>

        </div>
        <!-- / Client Descriptors -->

        <!-- Business Tags -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Business Tags</h2>

          <!-- Tags Container -->
          <div class="flex flex-wrap gap-3 mb-8">
            <span *ngFor="let tag of businessTags$ | async"
                  class="inline-flex items-center px-4 py-2 bg-blue-50 text-secondary rounded-full text-sm font-medium">
              {{ tag }}
            </span>
          </div>

          <!-- Business Categories Grid -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

            <!-- Main Business Category -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">Main Business Category</h3>
              <p class="text-base text-secondary">{{ mainBusinessCategory$ | async }}</p>
            </div>

            <!-- Main Industry -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">Main Industry</h3>
              <p class="text-base text-secondary">{{ mainIndustry$ | async }}</p>
            </div>

            <!-- Main Sector -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary uppercase tracking-wider mb-2">Main Sector</h3>
              <p class="text-base text-secondary">{{ mainSector$ | async }}</p>
            </div>

          </div>

        </div>
        <!-- / Business Tags -->

        <!-- Assets -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Assets</h2>

          <!-- Assets Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

            <!-- Website -->
            <div class="bg-gray-50 rounded-xl p-6 flex items-start space-x-4">
              <div class="flex-shrink-0">
                <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-1 17.93c-3.94-.49-7-3.85-7-7.93 0-.62.08-1.21.21-1.79L9 15v1c0 1.1.9 2 2 2v1.93zm6.9-2.54c-.26-.81-1-1.39-1.9-1.39h-1v-3c0-.55-.45-1-1-1H8v-2h2c.55 0 1-.45 1-1V7h2c1.1 0 2-.9 2-2v-.41c2.93 1.19 5 4.06 5 7.41 0 2.08-.8 3.97-2.1 5.39z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-sm font-semibold text-secondary-900 mb-1">Website</h3>
                <a [href]="'https://' + (websiteUrl$ | async)" target="_blank" class="text-base text-blue-600 hover:text-blue-700 hover:underline">
                  {{ websiteUrl$ | async }}
                </a>
              </div>
            </div>

            <!-- Facebook -->
            <div class="bg-gray-50 rounded-xl p-6 flex items-start space-x-4">
              <div class="flex-shrink-0">
                <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-sm font-semibold text-secondary-900 mb-1">Facebook</h3>
                <a [href]="'https://' + (facebookUrl$ | async)" target="_blank" class="text-base text-blue-600 hover:text-blue-700 hover:underline">
                  {{ facebookUrl$ | async }}
                </a>
              </div>
            </div>

            <!-- LinkedIn -->
            <div class="bg-gray-50 rounded-xl p-6 flex items-start space-x-4">
              <div class="flex-shrink-0">
                <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-sm font-semibold text-secondary-900 mb-1">LinkedIn</h3>
                <a [href]="'https://' + (linkedinUrl$ | async)" target="_blank" class="text-base text-blue-600 hover:text-blue-700 hover:underline">
                  {{ linkedinUrl$ | async }}
                </a>
              </div>
            </div>

            <!-- Instagram -->
            <div class="bg-gray-50 rounded-xl p-6 flex items-start space-x-4">
              <div class="flex-shrink-0">
                <svg class="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zM5.838 12a6.162 6.162 0 1 1 12.324 0 6.162 6.162 0 0 1-12.324 0zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm4.965-10.405a1.44 1.44 0 1 1 2.881.001 1.44 1.44 0 0 1-2.881-.001z"/>
                </svg>
              </div>
              <div class="flex-1">
                <h3 class="text-sm font-semibold text-secondary-900 mb-1">Instagram</h3>
                <a [href]="'https://' + (instagramUrl$ | async)" target="_blank" class="text-base text-blue-600 hover:text-blue-700 hover:underline">
                  {{ instagramUrl$ | async }}
                </a>
              </div>
            </div>


          </div>

        </div>
        <!-- / Assets -->

        <!-- Industry Snapshot -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Industry Snapshot</h2>

          <!-- Criteria Explanation Box -->
          <div class="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <h3 class="text-base font-semibold text-secondary-900 mb-2">Criteria Explanation</h3>
            <p class="text-base text-secondary">
              We analyzed companies in your industry using the following criteria: similar business models, comparable target markets, overlapping technology stacks, and competitive positioning. This analysis helps identify your competitive landscape and market opportunities.
            </p>
          </div>

          <!-- Competitor Stats Grid -->
          <div class="grid grid-cols-1 md:grid-cols-2 gap-6">

            <!-- Total Competitors in Location -->
            <div class="bg-blue-600 rounded-xl p-8 text-center">
              <h3 class="text-white text-base font-medium mb-4">Total Competitors in Location</h3>
              <p class="text-white text-4xl font-bold mb-2">{{ totalCompetitorsLocation$ | async }}</p>
              <p class="text-blue-100 text-sm">Identified companies</p>
            </div>

            <!-- Total Competitors in Continent -->
            <div class="bg-blue-600 rounded-xl p-8 text-center">
              <h3 class="text-white text-base font-medium mb-4">Total Competitors in Continent</h3>
              <p class="text-white text-4xl font-bold mb-2">{{ totalCompetitorsContinent$ | async }}</p>
              <p class="text-blue-100 text-sm">Identified companies</p>
            </div>

          </div>

        </div>
        <!-- / Industry Snapshot -->

        <!-- Geographic Dispersion -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Geographic Dispersion</h2>

          <!-- Top Cities -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-secondary-900 mb-4">Top 10 Cities</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div *ngFor="let city of topCities$ | async" class="bg-gray-50 rounded-lg p-4">
                <div class="flex justify-between items-center">
                  <span class="text-secondary font-medium">{{ city.city }}</span>
                  <span class="text-blue-600 font-bold">{{ city.percentage }}%</span>
                </div>
              </div>
            </div>
          </div>

          <!-- Country Breakdown -->
          <div class="mb-8">
            <h3 class="text-lg font-semibold text-secondary-900 mb-4">Country Breakdown</h3>
            <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              <div *ngFor="let country of countryBreakdown$ | async" class="bg-gray-50 rounded-lg p-4">
                <div class="flex justify-between items-center">
                  <span class="text-secondary font-medium">{{ country.country }}</span>
                  <span class="text-blue-600 font-bold">{{ country.percentage }}%</span>
                </div>
              </div>
            </div>
          </div>

        </div>
        <!-- / Geographic Dispersion -->

        <!-- Company Age Analysis -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Company Age Analysis</h2>

          <!-- Average Year Founded -->
          <div class="bg-blue-50 border border-blue-200 rounded-xl p-6 mb-6">
            <h3 class="text-base font-semibold text-secondary-900 mb-2">Average Year Founded</h3>
            <p class="text-2xl font-bold text-blue-600">{{ averageYearFounded$ | async }}</p>
          </div>

          <!-- Age Distribution -->
          <div>
            <h3 class="text-lg font-semibold text-secondary-900 mb-4">Age Distribution by Decade</h3>
            <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              <div *ngFor="let item of companyAgeDistribution$ | async | keyvalue" class="bg-gray-50 rounded-lg p-4">
                <div class="text-center">
                  <div class="text-secondary font-medium mb-1">{{ item.key }}</div>
                  <div class="text-blue-600 font-bold text-lg">{{ item.value }}%</div>
                </div>
              </div>
            </div>
          </div>

        </div>
        <!-- / Company Age Analysis -->

        <!-- Trend Analysis -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Trend Analysis</h2>

          <!-- Trends Grid -->
          <div class="grid grid-cols-1 md:grid-cols-3 gap-6">

            <!-- Top Description Keywords -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary-600 uppercase tracking-wider mb-4">Top Description Keywords</h3>
              <div class="flex flex-wrap gap-2">
                <span *ngFor="let keyword of topDescriptionKeywords$ | async"
                      class="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 text-secondary rounded-full text-sm">
                  {{ keyword }}
                </span>
              </div>
            </div>

            <!-- Top Business Tags -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary-600 uppercase tracking-wider mb-4">Top Business Tags</h3>
              <div class="flex flex-wrap gap-2">
                <span *ngFor="let tag of topBusinessTagsTrend$ | async" class="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 text-secondary rounded-full text-sm">
                  {{ tag }}
                </span>
              </div>
            </div>

            <!-- Tech Mentions -->
            <div class="bg-gray-50 rounded-xl p-6">
              <h3 class="text-sm font-semibold text-secondary-600 uppercase tracking-wider mb-4">Tech Mentions</h3>
              <div class="flex flex-wrap gap-2">
                <span *ngFor="let tech of techMentions$ | async" class="inline-flex items-center px-3 py-1.5 bg-white border border-gray-200 text-secondary rounded-full text-sm">
                  {{ tech }}
                </span>
              </div>
            </div>

          </div>

        </div>
        <!-- / Trend Analysis -->

        <!-- Next Step -->
        <div class="space-y-6 bg-white rounded-xl p-6 mb-6">

          <!-- Section Title -->
          <h2 class="text-xl font-semibold text-secondary-900">Next Steps</h2>

          <!-- Next Steps Description -->
          <p class="text-base text-secondary leading-relaxed mb-6">
            In our next phase, we'll define your Ideal Customer Profile in Sales Navigator and pinpoint the highest-ROI segments for {{client_name$ | async}},
            so we can discover the lowest-hanging fruit for your {{agency_service$ | async}}.
          </p>

          <!-- Ready to Take Action Box -->
          <div class="bg-blue-600 rounded-xl p-8">
            <h3 class="text-white text-xl font-semibold mb-4">Ready to take the next step?</h3>
            <p class="text-blue-100 text-base mb-6">
              If you'd like to discuss this document or any ideas in more detail, here's my Calendly link:
            </p>
            <button class="bg-white text-blue-600 font-medium px-6 py-3 rounded-lg hover:bg-blue-50 transition-colors">
              Book Your Strategy Session
            </button>
          </div>

        </div>
        <!-- // Next Step -->

      </div>

    </div>

  </div>

</div>


